<template>
  <div class="cwp">
    <div v-if="loading && !data" class="cwp-state">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      {{ $t("webProfile.searching") }}
    </div>

    <p v-else-if="status === 'no_key'" class="cwp-state">{{ $t("webProfile.noKey") }}</p>
    <p v-else-if="status === 'no_name'" class="cwp-state">{{ $t("webProfile.noName") }}</p>
    <p v-else-if="failed && !found" class="cwp-state text-danger">{{ $t("webProfile.error") }}</p>
    <p v-else-if="data && !found" class="cwp-state">{{ $t("webProfile.notFound") }}</p>

    <template v-if="found">
      <p v-if="headline" class="cwp-activity">{{ headline }}</p>
      <p v-if="profile.summary" class="cwp-summary">{{ profile.summary }}</p>

      <dl v-if="facts.length" class="cwp-facts">
        <template v-for="f in facts">
          <dt :key="f.key + '-dt'"><i :class="f.icon" aria-hidden="true"></i></dt>
          <dd :key="f.key + '-dd'" :class="{ 'cwp-italic': f.italic }">
            <a v-if="f.href" :href="f.href" target="_blank" rel="noopener noreferrer">{{ f.text }}</a>
            <template v-else>{{ f.text }}</template>
          </dd>
        </template>
      </dl>

      <div v-for="g in groups" :key="g.key" class="cwp-group">
        <div class="cwp-label">
          {{ g.label }}
          <!-- Producto: lo sugerido se carga en su campo (origin, presentation, uses). -->
          <button
            v-if="g.field && pendingOf(g).length > 1"
            type="button"
            class="cwp-addall"
            :disabled="!!applying"
            @click="apply(g.field, pendingOf(g))"
          >
            {{ $t("webProfile.addAll") }}
          </button>
        </div>
        <div class="cwp-chips">
          <template v-for="x in g.items">
            <span
              v-if="g.field && isApplied(g.field, x)"
              :key="g.key + '-' + x"
              class="cwp-chip cwp-chip--applied"
              :title="$t('webProfile.inProduct')"
            >
              <i class="fas fa-check" aria-hidden="true"></i> {{ x }}
            </span>
            <button
              v-else-if="g.field"
              :key="g.key + '-' + x"
              type="button"
              class="cwp-chip cwp-chip--add"
              :disabled="!!applying"
              :title="$t('webProfile.addToProduct')"
              @click="apply(g.field, [x])"
            >
              <i class="fas" :class="applying === g.field ? 'fa-spinner fa-spin' : 'fa-plus'" aria-hidden="true"></i> {{ x }}
            </button>
            <span v-else :key="g.key + '-' + x" class="cwp-chip">{{ x }}</span>
          </template>
        </div>
      </div>

      <div v-if="(profile.links || []).length" class="cwp-group">
        <div class="cwp-label">{{ $t("webProfile.links") }}</div>
        <ul class="cwp-links">
          <li v-for="l in profile.links" :key="l.url">
            <a :href="l.url" target="_blank" rel="noopener noreferrer">{{ l.label }}</a>
            <span class="cwp-host">{{ hostOf(l.url) }}</span>
          </li>
        </ul>
      </div>
    </template>

    <div v-if="data && status !== 'no_key' && status !== 'no_name'" class="cwp-foot">
      <span v-if="data.fetchedAt" class="cwp-foot__when">
        {{ $t("webProfile.searched", { date: formatDate(data.fetchedAt) }) }}
        <template v-if="(data.sources || []).length">
          · <a href="#" @click.prevent="showSources = !showSources">{{ $t("webProfile.sources", { n: data.sources.length }) }}</a>
        </template>
      </span>
      <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="loading" @click="load(true)">
        <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }" aria-hidden="true"></i>
        {{ $t("webProfile.searchAgain") }}
      </button>
    </div>
    <ul v-if="showSources" class="cwp-sources">
      <li v-for="s in data.sources" :key="s.url">
        <a :href="s.url" target="_blank" rel="noopener noreferrer">{{ s.title }}</a>
        <span class="cwp-host">{{ s.source }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * «En la web» de la ficha (contactos y productos). Al montarse (cuando alguien abre la
 * ficha) el back busca solo si nunca se buscó; después devuelve lo guardado. «Volver a
 * buscar» lo rehace. Si de paso le puso la imagen (logo del contacto, foto del
 * producto), se avisa con `image-set` para recargar el item.
 *
 * Cada modelo devuelve su perfil; acá solo se dice qué va como dato y qué como lista.
 */
import Swal from "sweetalert2";
import { wencesApi } from "@/api/wencesApi";
import { getApiErrorMessage } from "@/utils/dynamicModelValidationSwal";

const ENDPOINTS = { Contact: "/api/contact-web-profile", Product: "/api/product-web-profile" };
/** Grupo de la tarjeta → campo del producto donde se carga (migración 1.4.74). */
const PRODUCT_FIELDS = { origin: "origin", presentations: "presentation", uses: "uses" };
const FIELD_LIST_TYPES = { origin: "country", presentation: "productpresentation", uses: "productuse" };

export default {
  name: "WebProfileCard",
  props: {
    modelName: { type: String, required: true },
    itemId: { type: String, required: true },
    /** El item y las listas, para saber qué sugerencia ya está cargada en su campo. */
    item: { type: Object, default: null },
    lists: { type: Array, default: () => [] },
  },
  data() {
    return {
      data: null,
      loading: false,
      failed: false,
      showSources: false,
      /** Campo que se está cargando (origin, presentation, uses). */
      applying: null,
    };
  },
  computed: {
    status() {
      return (this.data && this.data.status) || "";
    },
    found() {
      return !!(this.data && this.data.found);
    },
    profile() {
      return (this.data && this.data.profile) || {};
    },
    isProduct() {
      return this.modelName === "Product";
    },
    headline() {
      return this.isProduct ? this.profile.kind : this.profile.activity;
    },
    facts() {
      const p = this.profile;
      const list = this.isProduct
        ? [
            p.scientific && { key: "sci", icon: "fas fa-dna", text: p.scientific, italic: true },
            p.presentation && { key: "pres", icon: "fas fa-box", text: p.presentation },
            p.wikipedia && { key: "wiki", icon: "fab fa-wikipedia-w", text: "Wikipedia", href: p.wikipedia },
          ]
        : [
            p.website && { key: "web", icon: "fas fa-globe", text: this.hostOf(p.website), href: p.website },
            p.location && { key: "loc", icon: "fas fa-map-marker-alt", text: p.location },
            p.founded && { key: "year", icon: "fas fa-flag", text: this.$t("webProfile.founded", { year: p.founded }) },
            p.size && { key: "size", icon: "fas fa-users", text: p.size },
          ];
      return list.filter(Boolean);
    },
    groups() {
      const keys = this.isProduct
        ? ["otherNames", "origin", "presentations", "uses"]
        : ["products", "markets"];
      return keys
        .filter((k) => Array.isArray(this.profile[k]) && this.profile[k].length)
        .map((k) => ({
          key: k,
          label: this.$t(`webProfile.${k}`),
          items: this.profile[k],
          field: this.isProduct ? PRODUCT_FIELDS[k] || null : null,
        }));
    },
    /** Por campo, las etiquetas (en minúscula) de lo que el producto ya tiene. */
    appliedLabels() {
      const out = {};
      Object.entries(FIELD_LIST_TYPES).forEach(([field, listType]) => {
        const list = this.lists.find((l) => l.type === listType);
        const byId = new Map(((list && list.values) || []).map((v) => [String(v._id), v.label]));
        const raw = this.item && this.item[field];
        const ids = (Array.isArray(raw) ? raw : raw ? [raw] : []).map((v) => String((v && v._id) || v));
        out[field] = new Set(ids.map((id) => String(byId.get(id) || "").toLowerCase()).filter(Boolean));
      });
      return out;
    },
    endpoint() {
      return `${ENDPOINTS[this.modelName]}/${this.itemId}`;
    },
  },
  watch: {
    endpoint() {
      this.data = null;
      this.showSources = false;
      this.load(false);
    },
  },
  mounted() {
    this.load(false);
  },
  methods: {
    async load(force) {
      const url = this.endpoint;
      this.loading = true;
      this.failed = false;
      try {
        const params = { lang: this.$i18n.locale };
        const { data } = force
          ? await wencesApi.post(`${url}/refresh`, null, { params })
          : await wencesApi.get(url, { params: { ...params, _: Date.now() } });
        if (url !== this.endpoint) return;
        const d = (data && data.data) || {};
        this.failed = d.status === "error";
        this.data = d;
        if (d.imageSet) this.$emit("image-set");
      } catch (e) {
        if (url === this.endpoint) this.failed = true;
      } finally {
        if (url === this.endpoint) this.loading = false;
      }
    },
    isApplied(field, label) {
      const set = this.appliedLabels[field];
      return !!set && set.has(String(label).toLowerCase());
    },
    pendingOf(g) {
      return g.items.filter((x) => !this.isApplied(g.field, x));
    },
    /**
     * Carga en el campo del producto lo que el usuario confirmó: el back da los ids de
     * lista (crea presentaciones y usos nuevos) y la ficha guarda con su guardado normal.
     */
    async apply(field, labels) {
      if (this.applying || !labels.length) return;
      this.applying = field;
      try {
        const { data } = await wencesApi.post(
          "/api/product-web-profile/resolve-list-values",
          { field, labels },
          { params: { lang: this.$i18n.locale } }
        );
        const d = (data && data.data) || {};
        if (d.created && d.created.length) this.$emit("lists-changed");
        if (d.ids && d.ids.length) {
          await new Promise((resolve) => this.$emit("apply", { key: field, ids: d.ids, done: resolve }));
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: this.$t("webProfile.applyError"), text: getApiErrorMessage(e) || "" });
      } finally {
        this.applying = null;
      }
    },
    hostOf(url) {
      try {
        return new URL(url).hostname.replace(/^www\./, "");
      } catch (e) {
        return url;
      }
    },
    formatDate(value) {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString(this.$i18n.locale, { day: "numeric", month: "short", year: "numeric" });
    },
  },
};
</script>

<style scoped>
.cwp-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.875rem;
  color: var(--bs-secondary-color, #6c757d);
}
.cwp-activity {
  margin: 0 0 0.25rem;
  font-weight: 600;
  color: var(--bs-primary);
}
.cwp-summary {
  margin: 0 0 0.75rem;
  font-size: 0.875rem;
  line-height: 1.45;
}
.cwp-facts {
  display: grid;
  grid-template-columns: 1.1rem minmax(0, 1fr);
  gap: 0.35rem 0.5rem;
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
}
.cwp-facts dt {
  font-weight: normal;
  color: var(--bs-secondary-color, #6c757d);
  text-align: center;
}
.cwp-facts dd {
  margin: 0;
  overflow-wrap: anywhere;
}
.cwp-italic {
  font-style: italic;
}
.cwp-group {
  margin-bottom: 0.75rem;
}
.cwp-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color, #6c757d);
  margin-bottom: 0.3rem;
}
.cwp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}
.cwp-chip {
  font-size: 0.78rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: rgba(var(--bs-primary-rgb), 0.08);
  color: var(--bs-body-color);
}
/* Sugerencia que se puede cargar en el producto. */
.cwp-chip--add {
  border: 1px dashed rgba(var(--bs-primary-rgb), 0.5);
  background: transparent;
  cursor: pointer;
}
.cwp-chip--add:hover:not(:disabled) {
  background: rgba(var(--bs-primary-rgb), 0.12);
}
.cwp-chip--add i,
.cwp-chip--applied i {
  font-size: 0.65rem;
  margin-right: 0.15rem;
}
/* Ya está en el producto. */
.cwp-chip--applied {
  background: rgba(var(--bs-success-rgb), 0.14);
}
.cwp-chip--applied i {
  color: var(--bs-success);
}
.cwp-addall {
  margin-left: 0.5rem;
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
  color: var(--bs-primary);
}
.cwp-links,
.cwp-sources {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.85rem;
}
.cwp-sources {
  margin-top: 0.6rem;
  font-size: 0.78rem;
}
.cwp-host {
  margin-left: 0.35rem;
  font-size: 0.72rem;
  color: var(--bs-secondary-color, #6c757d);
}
.cwp-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}
.cwp-foot__when {
  font-size: 0.75rem;
  color: var(--bs-secondary-color, #6c757d);
}
.cwp-foot .btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-left: auto;
}
</style>
